import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Form,
  Table,
  Modal,
  Row,
  Col,
  Badge,
  Alert,
} from "react-bootstrap";
import {
  ActionType,
  SelectorSource,
} from "../../interfaces/state-machine/state-machine";
import { PageType } from "../../interfaces/state-machine/page-recognise";
import { IMerchantSimple } from "../../interfaces";

interface SelectorSettingsProps {
  merchant: IMerchantSimple;
  onUpdate: (updatedSelectors: any) => void;
  isReadOnly?: boolean;
}

export const SelectorsSettings: React.FC<SelectorSettingsProps> = ({
  merchant,
  onUpdate,
  isReadOnly = false,
}) => {
  // Helper function to format a date for the input
  const formatDateForInput = (date: Date | string | undefined): string => {
    if (!date) return "";

    try {
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) return "";

      // Format as YYYY-MM-DD for input[type="date"]
      return d.toISOString().split("T")[0];
    } catch (e) {
      console.error("Error formatting date:", e);
      return "";
    }
  };
  // State for the currently edited selectors
  const [selectors, setSelectors] = useState<{
    [key in ActionType]?: Array<{
      selector: string;
      xpathSelector?: string;
      confidence: number;
      reasoning?: string;
      successCount: number;
      failureCount: number;
      lastWorked: Date;
      pageTypes?: PageType[];
      discoveredBy?: SelectorSource;
    }>;
  }>({});

  // State for the modal
  const [showModal, setShowModal] = useState(false);
  const [currentActionType, setCurrentActionType] = useState<ActionType | null>(
    null
  );
  const [currentSelectorIndex, setCurrentSelectorIndex] = useState<
    number | null
  >(null);
  const [editedSelector, setEditedSelector] = useState<any>({
    selector: "",
    xpathSelector: "",
    confidence: 0.5,
    reasoning: "",
    successCount: 0,
    failureCount: 0,
    lastWorked: new Date(),
    pageTypes: [],
    discoveredBy: SelectorSource.MANUAL,
  });

  // Load selectors from merchant data
  useEffect(() => {
    if (merchant && merchant.discoveredSelectors) {
      setSelectors(merchant.discoveredSelectors);
    }
  }, [merchant]);

  // Update parent component whenever selectors change
  useEffect(() => {
    onUpdate({ discoveredSelectors: selectors });
  }, [selectors, onUpdate]);

  // Handle opening the edit modal
  const handleEditSelector = (actionType: ActionType, index: number) => {
    setCurrentActionType(actionType);
    setCurrentSelectorIndex(index);

    const selectorToEdit = selectors[actionType]?.[index];
    if (selectorToEdit) {
      // Ensure proper date handling for lastWorked
      let lastWorked: Date;
      try {
        // Convert any date format to a proper Date object
        lastWorked =
          selectorToEdit.lastWorked instanceof Date
            ? selectorToEdit.lastWorked
            : new Date(selectorToEdit.lastWorked || Date.now());

        // Check if the date is valid
        if (isNaN(lastWorked.getTime())) {
          lastWorked = new Date();
          console.warn("Invalid date detected, using current date");
        }
      } catch (e) {
        lastWorked = new Date();
        console.warn("Invalid date format, using current date", e);
      }

      setEditedSelector({
        ...selectorToEdit,
        lastWorked,
        pageTypes: selectorToEdit.pageTypes || [],
      });
    }

    setShowModal(true);
  };

  // Handle creating a new selector
  const handleAddSelector = (actionType: ActionType) => {
    setCurrentActionType(actionType);
    setCurrentSelectorIndex(null);
    // Create fresh default values for a new selector
    setEditedSelector({
      selector: "",
      xpathSelector: "",
      confidence: 0.5,
      reasoning: "",
      successCount: 0,
      failureCount: 0,
      lastWorked: new Date(), // Always create with current date
      pageTypes: [],
      discoveredBy: SelectorSource.MANUAL,
    });
    setShowModal(true);
  };

  // Handle deleting a selector
  const handleDeleteSelector = (actionType: ActionType, index: number) => {
    const updatedSelectors = { ...selectors };
    if (updatedSelectors[actionType]) {
      updatedSelectors[actionType] = updatedSelectors[actionType]?.filter(
        (_, i) => i !== index
      );
      if (updatedSelectors[actionType]?.length === 0) {
        delete updatedSelectors[actionType];
      }
    }
    setSelectors(updatedSelectors);
  };

  // Handle saving a selector in the modal
  const handleSaveSelector = () => {
    if (currentActionType) {
      const updatedSelectors = { ...selectors };

      if (!updatedSelectors[currentActionType]) {
        updatedSelectors[currentActionType] = [];
      }

      // Ensure the lastWorked field is properly saved as a Date
      // This prevents issues when re-editing the selector
      const preparedSelector = {
        ...editedSelector,
        lastWorked: editedSelector.lastWorked.toISOString(),
      };

      if (currentSelectorIndex !== null) {
        // Edit existing
        if (updatedSelectors[currentActionType]) {
          updatedSelectors[currentActionType] = [
            ...updatedSelectors[currentActionType]!.slice(
              0,
              currentSelectorIndex
            ),
            preparedSelector,
            ...updatedSelectors[currentActionType]!.slice(
              currentSelectorIndex + 1
            ),
          ];
        }
      } else {
        // Add new
        updatedSelectors[currentActionType] = [
          ...(updatedSelectors[currentActionType] || []),
          preparedSelector,
        ];
      }

      setSelectors(updatedSelectors);
      setShowModal(false);
    }
  };

  // Helper to display the confidence as a color-coded badge
  const renderConfidenceBadge = (confidence: number) => {
    let variant = "danger";
    if (confidence >= 0.8) variant = "success";
    else if (confidence >= 0.5) variant = "warning";

    return <Badge bg={variant}>{(confidence * 100).toFixed(0)}%</Badge>;
  };

  // Helper to render a selector row
  const renderSelectorRow = (
    actionType: ActionType,
    selector: any,
    index: number
  ) => {
    return (
      <tr key={`${actionType}-${index}`}>
        <td>{index + 1}</td>
        <td>
          <code className="text-wrap">{selector.selector}</code>
          {selector.xpathSelector && (
            <div className="mt-1">
              <small className="text-muted">
                XPath: <code>{selector.xpathSelector}</code>
              </small>
            </div>
          )}
        </td>
        <td>{renderConfidenceBadge(selector.confidence)}</td>
        <td>
          {selector.successCount} / {selector.failureCount}
          <div className="mt-1">
            <small className="text-muted">
              Success Rate:{" "}
              {selector.successCount + selector.failureCount > 0
                ? (
                    (selector.successCount /
                      (selector.successCount + selector.failureCount)) *
                    100
                  ).toFixed(0)
                : 0}
              %
            </small>
          </div>
        </td>
        <td>{selector.discoveredBy}</td>
        <td>
          {selector.pageTypes && selector.pageTypes.length > 0 ? (
            selector.pageTypes.map((type: PageType) => (
              <Badge key={type} bg="info" className="me-1 mb-1">
                {type}
              </Badge>
            ))
          ) : (
            <span className="text-muted">All pages</span>
          )}
        </td>
        <td>{selector.lastWorked}</td>
        <td>
          {!isReadOnly && (
            <>
              <Button
                variant="outline-primary"
                size="sm"
                className="me-2"
                onClick={() => handleEditSelector(actionType, index)}
              >
                <i className="bi bi-pencil-square"></i>
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleDeleteSelector(actionType, index)}
              >
                <i className="bi bi-trash"></i>
              </Button>
            </>
          )}
        </td>
      </tr>
    );
  };

  // Helper to render the action type table
  const renderActionTypeTable = (actionType: ActionType) => {
    const selectorsList = selectors[actionType] || [];

    return (
      <Card className="mb-4" key={actionType}>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{actionType}</h5>
          {!isReadOnly && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleAddSelector(actionType)}
            >
              <i className="bi bi-plus-lg"></i> Add Selector
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          {selectorsList.length > 0 ? (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Selector</th>
                  <th>Confidence</th>
                  <th>Success/Failure</th>
                  <th>Source</th>
                  <th>Page Types</th>
                  <th>Last Worked</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectorsList.map((selector, index) =>
                  renderSelectorRow(actionType, selector, index)
                )}
              </tbody>
            </Table>
          ) : (
            <Alert variant="info">No selectors defined for {actionType}</Alert>
          )}
        </Card.Body>
      </Card>
    );
  };

  // Create a new ActionType
  const [newActionType, setNewActionType] = useState<string>("");

  const handleAddActionType = () => {
    if (
      newActionType &&
      !Object.values(ActionType).includes(newActionType as ActionType)
    ) {
      alert("Please select a valid ActionType from the dropdown");
      return;
    }

    if (newActionType && !((newActionType as ActionType) in selectors)) {
      setSelectors({
        ...selectors,
        [newActionType]: [],
      });
      setNewActionType("");
    }
  };

  // Render the modal
  const renderEditModal = () => {
    return (
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {currentSelectorIndex !== null ? "Edit" : "Add"} Selector for{" "}
            {currentActionType}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>CSS Selector</Form.Label>
                  <Form.Control
                    type="text"
                    value={editedSelector.selector}
                    onChange={(e) =>
                      setEditedSelector({
                        ...editedSelector,
                        selector: e.target.value,
                      })
                    }
                    placeholder="e.g., #add-to-cart-button, .checkout-btn"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>XPath Selector (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={editedSelector.xpathSelector || ""}
                    onChange={(e) =>
                      setEditedSelector({
                        ...editedSelector,
                        xpathSelector: e.target.value,
                      })
                    }
                    placeholder="e.g., //button[@id='checkout']"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Confidence ({(editedSelector.confidence * 100).toFixed(0)}%)
                  </Form.Label>
                  <Form.Range
                    value={editedSelector.confidence * 100}
                    onChange={(e) =>
                      setEditedSelector({
                        ...editedSelector,
                        confidence: Number(e.target.value) / 100,
                      })
                    }
                    min="0"
                    max="100"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Source</Form.Label>
                  <Form.Select
                    value={editedSelector.discoveredBy || SelectorSource.MANUAL}
                    onChange={(e) =>
                      setEditedSelector({
                        ...editedSelector,
                        discoveredBy: e.target.value,
                      })
                    }
                  >
                    {Object.values(SelectorSource).map((source) => (
                      <option key={source} value={source}>
                        {source}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Success Count</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={editedSelector.successCount || 0}
                    onChange={(e) =>
                      setEditedSelector({
                        ...editedSelector,
                        successCount: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Failure Count</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={editedSelector.failureCount || 0}
                    onChange={(e) =>
                      setEditedSelector({
                        ...editedSelector,
                        failureCount: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Last Worked</Form.Label>
              <Form.Control
                type="date"
                value={formatDateForInput(editedSelector.lastWorked)}
                onChange={(e) => {
                  const dateValue = e.target.value
                    ? new Date(e.target.value)
                    : new Date();
                  setEditedSelector({
                    ...editedSelector,
                    lastWorked: dateValue,
                  });
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Page Types</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {Object.values(PageType).map((pageType) => (
                  <Form.Check
                    key={pageType}
                    type="checkbox"
                    id={`pageType-${pageType}`}
                    label={pageType}
                    checked={
                      editedSelector.pageTypes?.includes(pageType) || false
                    }
                    onChange={(e) => {
                      const newPageTypes = e.target.checked
                        ? [...(editedSelector.pageTypes || []), pageType]
                        : (editedSelector.pageTypes || []).filter(
                            (type: PageType) => type !== pageType
                          );
                      setEditedSelector({
                        ...editedSelector,
                        pageTypes: newPageTypes,
                      });
                    }}
                  />
                ))}
              </div>
              <Form.Text className="text-muted">
                Select which page types this selector applies to. If none are
                selected, it will apply to all page types.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Reasoning/Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editedSelector.reasoning || ""}
                onChange={(e) =>
                  setEditedSelector({
                    ...editedSelector,
                    reasoning: e.target.value,
                  })
                }
                placeholder="Optional notes about why this selector works or how it was discovered"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveSelector}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <div className="selectors-settings">
      {!isReadOnly && (
        <div className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Add New Action Type</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-flex">
                <Form.Select
                  value={newActionType}
                  onChange={(e) => setNewActionType(e.target.value)}
                  className="me-2"
                >
                  <option value="">Select an Action Type</option>
                  {Object.values(ActionType).map((type) => (
                    <option
                      key={type}
                      value={type}
                      disabled={type in selectors}
                    >
                      {type} {type in selectors ? "(Already added)" : ""}
                    </option>
                  ))}
                </Form.Select>
                <Button
                  onClick={handleAddActionType}
                  disabled={
                    !newActionType || (newActionType as ActionType) in selectors
                  }
                >
                  Add
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>
      )}

      {Object.keys(selectors).length > 0 ? (
        <>
          {Object.keys(selectors).map((actionType) =>
            renderActionTypeTable(actionType as ActionType)
          )}

          {!isReadOnly && (
            <div className="d-flex justify-content-end mb-4">
              <Button variant="primary">Save All Changes</Button>
            </div>
          )}
        </>
      ) : (
        <Alert variant="info">
          No selectors have been defined for this merchant yet.
          {!isReadOnly &&
            " Use the 'Add New Action Type' option above to get started."}
        </Alert>
      )}

      {renderEditModal()}
    </div>
  );
};
